import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    CardActions,
    Button,
    Container,
    Paper,
    CardActionArea
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    Assessment as AssessmentIcon,
    AutoAwesome as AutoAwesomeIcon,
    CompareArrows as CompareIcon,
    LocalHospital as SusIcon,
    Settings as SettingsIcon,
    TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { jwtDecode } from 'jwt-decode';
import AnimatedText from '../layout/AnimatedText';

function Home({ handleLogout }) {
    const navigate = useNavigate();
    const theme = useTheme();

    useEffect(() => {
        const checkToken = () => {
            const storedToken = localStorage.getItem('token');

            if (storedToken) {
                try {
                    const decodedToken = jwtDecode(storedToken);
                    const tokenExpirationTime = decodedToken.exp * 1000;

                    if (tokenExpirationTime < Date.now()) {
                        handleLogout();
                        navigate('/login', { state: { message: 'Seu token expirou. Efetue login novamente.' } });
                    }
                } catch (error) {
                    handleLogout();
                    navigate('/login', { state: { message: 'Erro ao processar o token. Efetue login novamente.' } });
                }
            } else {
                handleLogout();
                navigate('/login', { state: { message: 'Por favor, efetue login.' } });
            }
        };

        checkToken();
    }, [navigate, handleLogout]);

    const dashboardItems = [
        {
            title: 'Auditoria Inteligente',
            description: 'Analise faturamentos TISS com inteligência artificial e detecção de riscos.',
            icon: <AssessmentIcon sx={{ fontSize: 40, color: '#6366f1' }} />,
            link: '/auditoria-tiss',
            buttonText: 'Acessar'
        },
        {
            title: 'Auditoria Customizada',
            description: 'Crie e execute regras de auditoria personalizadas e avançadas.',
            icon: <AutoAwesomeIcon sx={{ fontSize: 40, color: '#a855f7' }} />,
            link: '/auditoria-custom',
            buttonText: 'Configurar'
        },
        {
            title: 'Comparador Global',
            description: 'Compare procedimentos e tabelas de preços entre convênios de forma global.',
            icon: <CompareIcon sx={{ fontSize: 40, color: '#22c55e' }} />,
            link: '/comparador-procedimentos',
            buttonText: 'Comparar'
        },
        {
            title: 'Auditoria SUS AIH',
            description: 'Auditoria especializada em documentos e faturamentos SUS AIH.',
            icon: <SusIcon sx={{ fontSize: 40, color: '#ef4444' }} />,
            link: '/auditoria-sus',
            buttonText: 'Analisar'
        },
        {
            title: 'Configurações',
            description: 'Gerencie seu perfil, usuários e preferências do sistema.',
            icon: <SettingsIcon sx={{ fontSize: 40, color: '#64748b' }} />,
            link: '/configuracoes',
            buttonText: 'Ajustar'
        }
    ];

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                    Bem-vindo ao ConciliarApp
                </Typography>
                <AnimatedText text="Seu painel de controle financeiro inteligente." />
            </Box>

            <Grid container spacing={4}>
                {dashboardItems.map((item, index) => (
                    <Grid item xs={12} md={4} key={index}>
                        <Card
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'transform 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-5px)',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                                }
                            }}
                        >
                            <CardActionArea component={Link} to={item.link} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                                <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                                        {item.icon}
                                    </Box>
                                    <Typography gutterBottom variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                                        {item.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {item.description}
                                    </Typography>
                                </CardContent>
                                <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        sx={{ borderRadius: 20, px: 4, pointerEvents: 'none' }} // Disable button pointer events so the card click takes precedence
                                    >
                                        {item.buttonText}
                                    </Button>
                                </CardActions>
                            </CardActionArea>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mt: 6,
                    backgroundColor: 'rgba(26, 35, 126, 0.05)',
                    borderRadius: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                <Box>
                    <Typography variant="h6" sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                        Precisa de ajuda?
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Nossa equipe de suporte está pronta para auxiliar você.
                    </Typography>
                </Box>
                <Button
                    component={Link}
                    to="/contato"
                    variant="contained"
                    color="primary"
                    startIcon={<TrendingUpIcon />}
                >
                    Falar com Suporte
                </Button>
            </Paper>
        </Container>
    );
}

export default Home;
